/**
 * shrinkLog — a long-lived record of the ONE event we're hunting: text that
 * disappeared without the user deleting it.
 *
 * WHY (Walter 2026-09-04). The content trail works — it caught his paragraph
 * being cut and restored eight seconds later — but it keeps 60 versions, and
 * he burns through those in a few hours of normal editing. His losses are days
 * apart: *"these losses are very random and intermittent… it is often okay for
 * several days in a row."* So the instrument that finally sees the bug is also
 * the instrument that will have rolled over by the time he notices it.
 *
 * The fix isn't a bigger buffer — 30k-character versions get expensive fast,
 * and localStorage is shared with the offline queue, which holds real unsaved
 * content and must never be evicted for a diagnostic. The fix is to be
 * SELECTIVE: keep only the events that could be the bug, and keep those for a
 * long time.
 *
 * A "shrink" here is an incoming change — an `adopt` or a `merge`, never the
 * user's own `save` — that REMOVES lines from what this window last had. That
 * is rare (ordinary editing adds and rewrites; an incoming change that deletes
 * is unusual), so a couple of hundred entries spans weeks rather than hours.
 *
 * Each entry stores the removed lines and the window that did it, NOT the full
 * document — that's what keeps it small enough to retain.
 */

import { getWindowLabel } from './windowId';

const KEY = 'lists_shrink_log_v1';
const MAX_ENTRIES = 200;
// Far smaller than the content trail's budget: we store a handful of lines per
// event, not a 30k-character document.
const MAX_BYTES = 400 * 1024;
// A single removed line is usually the user's own edit echoing back. Two or
// more removed at once is the shape we care about. Kept low deliberately —
// Walter's first reported loss was "a couple of words", and a couple of words
// on their own line is two lines.
const MIN_REMOVED_LINES = 2;
// Truncate any single stored line; a 2,000-character paragraph in one line
// would blow the budget on its own.
const MAX_LINE_CHARS = 300;

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function write(entries) {
  let out = entries.slice(-MAX_ENTRIES);
  try {
    while (out.length > 1 && JSON.stringify(out).length > MAX_BYTES) out = out.slice(1);
    localStorage.setItem(KEY, JSON.stringify(out));
  } catch { /* quota or private mode — a diagnostic never throws */ }
}

/**
 * noteShrink({ listId, reason, removed, beforeLines, afterLines })
 *
 * Called when an INCOMING change removed lines. Unlike the content trail this
 * is always on: it costs nothing when nothing shrinks, and the whole point is
 * to be recording before the user notices anything is wrong.
 */
export function noteShrink({ listId, reason, removed, beforeLines, afterLines }) {
  try {
    const lines = Array.isArray(removed) ? removed.filter(l => String(l).trim() !== '') : [];
    if (lines.length < MIN_REMOVED_LINES) return;
    const entries = read();
    entries.push({
      t: Date.now(),
      list: listId,
      reason: String(reason || ''),
      w: getWindowLabel(),
      before: beforeLines,
      after: afterLines,
      removed: lines.slice(0, 40).map(l => String(l).slice(0, MAX_LINE_CHARS)),
      more: Math.max(0, lines.length - 40),
    });
    write(entries);
  } catch { /* ignore */ }
}

export function readShrinkLog() {
  return read();
}

export function clearShrinkLog() {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}

/**
 * Paste-ready. Newest LAST so it reads chronologically like the other logs.
 */
export function formatShrinkLog() {
  const entries = read();
  if (!entries.length) {
    return 'shrink log is empty — no incoming change has removed lines on this device yet. '
      + '(That is the good outcome: it means nothing has silently eaten text since this was installed.)';
  }
  const out = [
    `listicity shrink log (${entries.length} events, oldest first)`,
    'Each entry is an INCOMING change (adopt/merge) that removed lines — never the user\'s own edit.',
    '',
  ];
  for (const e of entries) {
    const when = new Date(e.t).toISOString().replace('T', ' ').slice(0, 19);
    out.push(`──── ${when}  list=${e.list}  ${e.reason}  w=${e.w || '?'}  ${e.before}→${e.after} lines`);
    out.push(...e.removed.map(l => `  - ${l}`));
    if (e.more) out.push(`  … and ${e.more} more removed lines`);
    out.push('');
  }
  return out.join('\n');
}

/**
 * Lines in `before` that are not in `after`, counting duplicates. Same
 * consume-from-a-pool approach as contentTrail's diff, duplicated here on
 * purpose so a diagnostic never depends on another diagnostic.
 */
export function removedLines(beforeText, afterText) {
  const before = String(beforeText ?? '').split('\n');
  const after = String(afterText ?? '').split('\n');
  const pool = new Map();
  for (const l of after) pool.set(l, (pool.get(l) || 0) + 1);
  const removed = [];
  for (const l of before) {
    const c = pool.get(l) || 0;
    if (c > 0) pool.set(l, c - 1);
    else if (l.trim() !== '') removed.push(l);
  }
  return removed;
}

/** Line count. A helper so call sites don't repeat a newline literal. */
export function countLines(text) {
  return String(text ?? '').split('\n').length;
}
